import {BaseComponent} from '../base/component.base.js';
import template from './site-preview.template.html';
import styles from './site-preview.styles.css';
import {SitePreviewView, estimatedProgress} from '../../core/site-preview/site-preview.core.js';
import {t} from '../../i18n/i18n.js';

/** How often the progress bar moves while the preview is built, in milliseconds. */
const PROGRESS_TICK_MS = 250;

/** The translation key of the status line of each view; the error view shows its own message. */
const STATUS_KEYS = Object.freeze({
    [SitePreviewView.BUILDING]: 'sitePreview.building',
    [SitePreviewView.SLOW]: 'sitePreview.slow',
    [SitePreviewView.READY]: 'sitePreview.ready',
    [SitePreviewView.FAILED]: 'sitePreview.failed',
});

/**
 * The "view on your website" bar: asks for a preview of the site with every draft and, once it is
 * ready, links to it. It only shows the state it is given and emits `site-preview-request` from
 * both of its buttons; the driving adapter does the requests. While the preview is built a
 * progress bar estimates how far along it is from the time since the build started. The hint about
 * a previous version only shows for a preview built again with the refresh button, since only then
 * an open preview tab can still hold the old one.
 */
export class SitePreviewComponent extends BaseComponent {
    /** @type {import('../../core/site-preview/site-preview.core.js').SitePreviewState} */
    #state = {kind: SitePreviewView.IDLE, url: null};
    /** When the current build started (`Date.now()`), null while no preview is built. */
    #buildingSince = null;
    #progressTimer = null;
    /** Whether the latest request came from the refresh button. */
    #refreshed = false;

    connectedCallback() {
        this.loadTemplate(template, styles);
        this.$('#site-preview-action').addEventListener('click', () => {
            // While building the button only reads as disabled, so it keeps keyboard focus.
            if (this.#state.kind !== SitePreviewView.BUILDING) {
                this.#refreshed = false;
                this.emit('site-preview-request');
            }
        });
        this.$('#site-preview-refresh').addEventListener('click', () => {
            this.#refreshed = true;
            this.emit('site-preview-request');
        });
        this.#render();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#stopProgress();
    }

    /** @returns {import('../../core/site-preview/site-preview.core.js').SitePreviewState} */
    get state() {
        return this.#state;
    }

    /** @param {import('../../core/site-preview/site-preview.core.js').SitePreviewState} value */
    set state(value) {
        const building = value.kind === SitePreviewView.BUILDING;
        // Every check answers "building" again; only the first one starts the clock.
        if (!building) {
            this.#buildingSince = null;
        } else if (this.#buildingSince === null) {
            this.#buildingSince = Date.now();
        }
        this.#state = value;
        this.#render();
    }

    #stopProgress() {
        clearInterval(this.#progressTimer);
        this.#progressTimer = null;
    }

    #renderProgress() {
        const percent = estimatedProgress(Date.now() - this.#buildingSince);
        this.$('#site-preview-progress-fill').style.width = `${percent}%`;
        this.$('#site-preview-progress-track').setAttribute('aria-valuenow', String(percent));
        this.$('#site-preview-progress-value').textContent = `${percent}%`;
    }

    #render() {
        const status = this.$('#site-preview-status');
        if (!status) return;
        const {kind, url, message} = this.#state;
        const ready = kind === SitePreviewView.READY;

        const action = this.$('#site-preview-action');
        const refresh = this.$('#site-preview-refresh');
        const focused = this.shadowRoot.activeElement;
        action.hidden = ready;
        // aria-disabled instead of disabled keeps focus on the button; the status line says why.
        if (kind === SitePreviewView.BUILDING) {
            action.setAttribute('aria-disabled', 'true');
            action.setAttribute('aria-describedby', 'site-preview-status');
        } else {
            action.removeAttribute('aria-disabled');
            action.removeAttribute('aria-describedby');
        }
        const open = this.$('#site-preview-open');
        open.hidden = !ready;
        if (ready) {
            open.href = url;
        } else {
            open.removeAttribute('href');
        }
        refresh.hidden = !ready;
        this.$('#site-preview-hint').hidden = !(ready && this.#refreshed);

        const building = kind === SitePreviewView.BUILDING;
        this.$('#site-preview-progress').hidden = !building;
        if (building) {
            this.#renderProgress();
        }
        // A detached bar keeps no timer running; connecting it again renders and restarts it.
        if (building && this.isConnected) {
            this.#progressTimer ??= setInterval(() => this.#renderProgress(), PROGRESS_TICK_MS);
        } else {
            this.#stopProgress();
        }
        // A hidden button loses keyboard focus to the page; hand it to the button that takes its place.
        if (ready && focused === action) {
            open.focus();
        } else if (!ready && focused === refresh) {
            action.focus();
        }

        status.classList.toggle('is-error', kind === SitePreviewView.ERROR || kind === SitePreviewView.FAILED);
        const key = STATUS_KEYS[kind];
        if (key) {
            // Kept as data-i18n so a change of language translates the line again.
            status.dataset.i18n = key;
            status.textContent = t(key);
        } else {
            delete status.dataset.i18n;
            status.textContent = kind === SitePreviewView.ERROR ? message : '';
        }
    }
}

customElements.define('site-preview-bar', SitePreviewComponent);
